import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface Category {
  id: string;
  name: string;
  description?: string;
  parent_category_id?: string;
  image?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // For hierarchy relationships
  parent?: Category;
  children?: Category[];
  products_count?: number;
}

export interface CreateCategoryData {
  name: string;
  description?: string;
  parent_category_id?: string;
  image?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface UpdateCategoryData {
  name?: string;
  description?: string;
  parent_category_id?: string;
  image?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface CategoryHierarchy {
  id: string;
  name: string;
  image?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  children: CategoryHierarchy[];
  products_count: number;
}

const categoryAPI = {
  getCategories: async (params?: {
    pageNo?: number;
    pageSize?: number;
    include_hierarchy?: boolean;
    parent_only?: boolean;
    parentId?: string;
    topLevel?: boolean;
    is_active?: boolean;
    isActive?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ data: Category[]; total: number }> => {
    const response = await api.get("/categories", { params });
    const { data, total } = parsePaginatedList<Category>(response.data.data);
    return { data, total };
  },

  getCategoryHierarchy: async (params?: {
    is_active?: boolean;
    include_products_count?: boolean;
  }): Promise<CategoryHierarchy[]> => {
    const response = await api.get("/categories/hierarchy", { params });
    return response.data.data || [];
  },

  getParentCategories: async (params?: {
    is_active?: boolean;
  }): Promise<Category[]> => {
    const response = await api.get("/categories/parents", { params });
    return response.data.data || [];
  },

  getSubCategories: async (
    parentId: string,
    params?: {
      is_active?: boolean;
    }
  ): Promise<Category[]> => {
    const response = await api.get(`/categories/${parentId}/subcategories`, {
      params,
    });
    return response.data.data || [];
  },

  allsubcategories: async (
    parentId: string,
    params?: {
      is_active?: boolean;
    }
  ): Promise<Category[]> => {
    const response = await api.get(`/categories/${parentId}/allsubcategories`, {
      params,
    });
    return response.data.data || [];
  },

  getCategoryById: async (
    id: string,
    params?: {
      include_parent?: boolean;
      include_children?: boolean;
      include_products?: boolean;
    }
  ): Promise<Category> => {
    const response = await api.get(`/categories/${id}`, { params });
    return response.data.data;
  },

  createCategory: async (data: CreateCategoryData): Promise<Category> => {
    const response = await api.post("/categories", data);
    return response.data.data;
  },

  updateCategory: async (
    id: string,
    data: UpdateCategoryData
  ): Promise<Category> => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data.data;
  },

  updateCategoryOrder: async (
    orderData: {
      category_id: string;
      display_order: number;
    }[]
  ): Promise<void> => {
    await api.patch("/categories/bulk-order", { order_data: orderData });
  },

  toggleCategoryStatus: async (id: string): Promise<Category> => {
    const response = await api.patch(`/categories/${id}/toggle-status`);
    return response.data.data;
  },

  getProductsByCategory: async (
    id: string,
    params?: {
      include_subcategories?: boolean;
      product_type?: "GOODS" | "PREPARED";
      is_active?: boolean;
      availability?: string[];
    }
  ): Promise<any[]> => {
    const response = await api.get(`/categories/${id}/products`, { params });
    return response.data.data;
  },

  // Upload category image
  uploadCategoryImage: async (
    id: string,
    imageFile: File
  ): Promise<{ image_url: string }> => {
    const formData = new FormData();
    formData.append("image", imageFile);
    const response = await api.post(
      `/categories/${id}/upload-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data;
  },

  // Delete category image
  deleteCategoryImage: async (id: string): Promise<void> => {
    await api.delete(`/categories/${id}/image`);
  },

  // Validate category before deletion
  validateCategoryDeletion: async (
    id: string
  ): Promise<{
    can_delete: boolean;
    has_products: boolean;
    has_subcategories: boolean;
    products_count: number;
    subcategories_count: number;
  }> => {
    const response = await api.get(`/categories/${id}/validate-deletion`);
    return response.data.data;
  },

  // Move category to different parent
  moveCategory: async (id: string, newParentId?: string): Promise<Category> => {
    const response = await api.patch(`/categories/${id}/move`, {
      new_parent_id: newParentId,
    });
    return response.data.data;
  },

  // Delete category
  deleteCategory: async (id: string): Promise<void> => {
    await api.delete(`/categories/${id}`);
  },
};

export { categoryAPI };
